import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { flushSync } from "react-dom";
import Select, { components } from "react-select";
import { I18nContext } from "../../store/context/i18nContext";
import { SELECT_AUTOFILL_PROPS } from "../InputField/autofillProps";
import "./style.css";
import { useTranslation } from "../../store/context/TranslationContext";
import PreviousValue from "../InputField/PreviousValue";
import { buildAutomationChildTestId, resolveAutomationTestId } from "../InputField/testId";
import { withAutomationComponents } from "./automationComponents";

const fontSize = 14;
const hasExplicitValue = (candidate) => candidate !== undefined && candidate !== null && candidate !== "";
const normalizeOptionMatchValue = (candidate) => String(candidate ?? "").trim().replace(/\s+/g, " ").toLowerCase();
const isSameOptionValue = (leftValue, rightValue) =>
  normalizeOptionMatchValue(leftValue) === normalizeOptionMatchValue(rightValue);
const findSelectedOptionByValue = (options = [], rawValue) =>
  options.find((option) => isSameOptionValue(option?.value, rawValue) || option === rawValue) ?? "";
const findSelectedOptionByText = (options = [], rawText) => {
  const normalizedText = normalizeOptionMatchValue(rawText);
  if (!normalizedText) {
    return "";
  }

  return options.find((option) => {
    const normalizedLabel = normalizeOptionMatchValue(option?.label);
    const normalizedValue = normalizeOptionMatchValue(option?.value);
    return normalizedLabel === normalizedText || normalizedValue === normalizedText;
  }) ?? "";
};
const commitMatchedOptionSelection = ({
  matchedOption,
  latestValueRef,
  setValue,
  onChange,
  skipParentSyncOnNextValueEffectRef,
  lastAutofillMatchRef,
  flushUpdates = false,
}) => {
  if (!matchedOption || isSameOptionValue(latestValueRef.current?.value, matchedOption?.value)) {
    return false;
  }

  const performUpdate = () => {
    lastAutofillMatchRef.current = normalizeOptionMatchValue(matchedOption?.label ?? matchedOption?.value);
    skipParentSyncOnNextValueEffectRef.current = true;
    setValue(matchedOption);
    onChange(matchedOption);
  };

  if (flushUpdates) {
    flushSync(performUpdate);
  } else {
    performUpdate();
  }

  return true;
};

const Menu = (props) => {
  return (
    <Fragment>
      <components.Menu {...props} className="w-100">
        <div className="w-100">
          {props?.selectProps.fetchingData ? (
            <span className="fetching">Fetching data...</span>
          ) : (
            <div>{props?.children}</div>
          )}

          <div
            className={"change-data d-flex justify-content-center w-100 "}
            onClick={props?.selectProps.changeOptionsData}
          >
            {/* <props.selectProps.bottomBarChildren /> */}
          </div>
        </div>
      </components.Menu>
    </Fragment>
  );
};

export const SelectField = (props) => {
  const {
    wrapperStyle,
    data,
    asLabel,
    inline,
    className,
    containerStyle,
    suggestion,
    innerContainerStyle,
    isDisabled,
    onClickLeft,
    LeftComponent,
    placeholder,
    label,
    noLabel,
    showBottomBar,
    desc,
    // bottomBarChildren,
    changeOptionsData,
    renderFunc,
    showClear,
    onMenuScrollToBottom,
    plain,
    onInputChange,
    required,
    RightComponent,
    onClickRight,
    showErr,
    error,
    flexible,
    AddEntityComponent,
    onChange = () => { },
    style,
    innerContainerClassName,
    containerClassName,
    onBlur,
    inputValue,
    inputGroup,
    outline,
    InputGroupChild,
    filterOption,
    dropDownMenuWidth,
    formatOptionLabel,
    labelStyle,
    menuPlacement = "auto",
    defaultValue = "",
    prevValue,
    suppressPreviousValue = false,
  } = props;

  const [showAddComponent, setShowAddComponent] = useState(false);
  const [onFocus, setOnFocus] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [value, setValue] = useState(
    data?.find((d) => (d?.value ?? "")?.toString() === (props?.value ?? " ")?.toString()) ?? ""
  );
  const { t, } = useTranslation();

  const selectRef = useRef(null);
  const selectWrapperRef = useRef(null);
  const fieldTestId = resolveAutomationTestId(props["data-testid"], props.id, props.name);
  const skipParentSyncOnNextValueEffectRef = useRef(false);
  const latestValueRef = useRef(value);
  const lastAutofillMatchRef = useRef("");
  // Read the parent onChange through a ref so the value->parent sync effect below does NOT depend on
  // it. onChange is a new inline function on every parent render; listing it as a dependency re-ran
  // that effect every render and, with an async parent onChange, re-fired it in a loop ("Maximum
  // update depth exceeded", country field flickering).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueToLabelMap = useMemo(() => Object.fromEntries(data?.map(({ value, label }) => [value, label])), [data]);

  // useEffect(() => {
  //   if (defaultValue && (!props?.value || props?.value == undefined || props?.value == null ) ) {
  //     let selected = data?.find((d) => d.value === defaultValue || d === defaultValue);
  //     if (selected) {
  //       setValue(selected);
  //       onChange(selected);
  //     }
  //   }
  // }, [defaultValue]);

  useEffect(() => {
    if (props.shouldFocus && selectRef.current && data?.length) {
      setTimeout(() => {
        selectRef.current.focus();
        setMenuOpen(true);
      }, 0); 
    }
  }, [props.shouldFocus, data?.length]);

  useEffect(() => {
    latestValueRef.current = value;
    lastAutofillMatchRef.current = normalizeOptionMatchValue(value?.label ?? value?.value);
  }, [value]);

  useEffect(() => {
    if (!data?.length || isSameOptionValue(props?.value, value?.value)) {
      return;
    }

    // This sync originates FROM the parent (props.value changed externally, e.g. the address
    // autocomplete writing a new country). Without the skip flag, the value->parent effect
    // below still runs in this same commit with the NOT-YET-SYNCED internal option and echoes
    // the stale value back to the parent; with an async parent onChange the two writers then
    // ping-pong forever ("Maximum update depth exceeded", country visibly toggling). The flag
    // is consumed by the value-effect in this same commit, so user picks (which go through
    // setValue in the select's own onChange) still propagate normally.
    if (hasExplicitValue(props?.value)) {
      const selected = findSelectedOptionByValue(data, props?.value);
      if (selected) {
        skipParentSyncOnNextValueEffectRef.current = true;
        setValue(selected);
        return;
      }
    }

    if (value !== "") {
      skipParentSyncOnNextValueEffectRef.current = true;
    }
    setValue("");
  }, [props?.value, data]);

  useEffect(() => {
    if (!data?.length || isSameOptionValue(props?.value, value?.value)) {
      return;
    }

    if (skipParentSyncOnNextValueEffectRef.current) {
      skipParentSyncOnNextValueEffectRef.current = false;
      return;
    }

    if (!value) {
      if (value === 0) onChangeRef.current(0);
      return;
    }

    onChangeRef.current(value);
  }, [value, data, props?.value]);

  useEffect(() => {
    if (!data?.length || typeof window === "undefined") {
      return undefined;
    }

    const syncAutofilledOption = () => {
      const inputNode = selectWrapperRef.current?.querySelector("input");
      if (!inputNode || document.activeElement === inputNode) {
        return;
      }

      const rawText = normalizeOptionMatchValue(inputNode.value);
      if (!rawText || rawText === lastAutofillMatchRef.current) {
        return;
      }

      const matchedOption = findSelectedOptionByText(data, rawText);
      commitMatchedOptionSelection({
        matchedOption,
        latestValueRef,
        setValue,
        onChange,
        skipParentSyncOnNextValueEffectRef,
        lastAutofillMatchRef,
      });
    };

    syncAutofilledOption();

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      syncAutofilledOption();
      attempts += 1;
      if (attempts >= 20) {
        window.clearInterval(intervalId);
      }
    }, 250);

    const handleWindowFocus = () => {
      window.setTimeout(syncAutofilledOption, 120);
    };

    window.addEventListener("pageshow", handleWindowFocus);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("pageshow", handleWindowFocus);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [data, onChange]);

  if (asLabel) {
    return <div>{value.label}</div>;
  }
  return (
    <React.Fragment>
 
      <div
        data-testid={fieldTestId}
        style={{
          ...wrapperStyle,
          ...style,
        }}
        className={`${inline ? "" : "selectfield__wrapper "} ${className}`}
      >
  
        { !noLabel && !(inline || outline || plain) ? (
          <div
            data-testid={buildAutomationChildTestId(fieldTestId, "label")}
            style={{  fontSize: 16, margin: "0px 0px 5px 5px", fontWeight: "500", display: "block", ...labelStyle }}
          >
            {t(label)}
            {required && <font color="red">*</font>}
          </div>
        ) : <div style={{marginTop : inline ? "0px" : "0px"}} ></div>}



        <div
          style={containerStyle}
          className={
            ` ${!(plain || outline || inline) ? "d-flex justify-content-center align-items-center " : " "}      selectfield__container ` +
            (plain && !onFocus ? "selectfield__plain " : "") +
            containerClassName
          }
        >
   
          {suggestion && (
            <b className="selectfield__suggestion" data-testid={buildAutomationChildTestId(fieldTestId, "suggestion")}>{suggestion}</b>
          )}
          <div
            id="x1"
            className={
              inline ? " selectfield_inline__innercontainer " :  " selectfield__innercontainer " +  " d-flex  flex-1 " + innerContainerClassName
            }
            style={{
              ...innerContainerStyle,
              backgroundColor: prevValue?.has ? "#FFF9D6" : isDisabled ? "#F6F6F6" : "",
              cursor: isDisabled ? "not-allowed" : "default",
            }}
          >
            {LeftComponent && (
              <div
                id="x2"
                data-testid={buildAutomationChildTestId(fieldTestId, "left")}
                onClick={(e) =>
                  onClickLeft ? onClickLeft(e) : null
                }
                className={
                  onClickLeft
                    ? "component_pointer Selectfield__leftComponent"
                    : "selectfield__leftComponent"
                }
                style={{ height: inline ? "19px" : "28px" }}
              >
                < LeftComponent />
              </div>
            )}
            <div
              ref={selectWrapperRef}
              className={InputGroupChild ? "input-group  w-100 " : inline ? "input-group" : "selectfield x1"}
              id="x5"
            >
              {inline && (
                <div
                  style={{ height: "19px", border: "none", fontSize: 11 }}
                  className="input-group-text"
                >
                  {t(label)}
                </div>
              )}
              {InputGroupChild && (
                <div
                  style={{ border: "none", fontSize: 11 }}
                  className="input-group-text grouped-input-select-group px-2  "
                  data-testid={buildAutomationChildTestId(fieldTestId, "input-group")}
                >
                  < InputGroupChild />
                </div>
              )}
              <Select
                inputId={props?.id || fieldTestId}
                instanceId={props?.id || fieldTestId}
                {...SELECT_AUTOFILL_PROPS}
                formatOptionLabel={formatOptionLabel}
                {...(filterOption && { filterOption: filterOption })}
                automationTestId={fieldTestId}
                components={withAutomationComponents(
                  showBottomBar
                    ? value
                      ? {
                        IndicatorSeparator: () => null,
                        components,
                        Menu,
                        DropdownIndicator: () => null,
                      }
                      : { IndicatorSeparator: () => null, Menu }
                    : value
                      ? {
                        IndicatorSeparator: () => null,
                        DropdownIndicator: () => null,
                      }
                      : { IndicatorSeparator: () => null }
                )}
                // bottomBarChildren={bottomBarChildren}
                changeOptionsData={changeOptionsData}
                optionComponent={renderFunc}
                menuPlacement={"bottom"}
                menuPosition={'auto'}
                {...(props?.shouldFocus && { menuIsOpen: menuOpen })}
                onMenuScrollToBottom={onMenuScrollToBottom}
                onInputChange={onInputChange}
                setValue={inputValue}

                value={value}
                styles={
                  InputGroupChild ?
                    stylesForInputGroupChild :
                    inline ?
                      stylesForInline :
                      inputGroup ?
                        stylesForInputGroup :
                        stylesForMain}
                ref={selectRef}
                isClearable={showClear && !plain && !inline}
                onFocus={() => {
                  setOnFocus(true);
                  setMenuOpen(true)
                  if (props?.onFocus) props?.onFocus();
                }}
                onBlur={() => {
                  const rawText = normalizeOptionMatchValue(
                    selectWrapperRef.current?.querySelector("input")?.value
                  );
                  const matchedOption = findSelectedOptionByText(data, rawText);
                  commitMatchedOptionSelection({
                    matchedOption,
                    latestValueRef,
                    setValue,
                    onChange,
                    skipParentSyncOnNextValueEffectRef,
                    lastAutofillMatchRef,
                    flushUpdates: true,
                  });
                  setMenuOpen(false);
                  setOnFocus(false);
                  if (onBlur) onBlur();
                }}
                options={data}
                onChange={(v) => {

                  if (props?.shouldFocus) {
                
                    setMenuOpen(false);
                    if (selectRef.current) {
                      selectRef.current.blur();
                    }
                  }
                  (v ? setValue(v) : setValue(""))
                }
                }
                className={
                  InputGroupChild ? "form-control px-0 py-0 border-0" :
                    !inline
                      ? "selectfield__select"
                      : "form-control px-0 py-0 rounded-right-1"
                }
                placeholder={
                  noLabel || onFocus || plain || inline || !(inline || outline || plain)
                    ? t(placeholder)
                    : ""
                }
                isDisabled={isDisabled || false}
              />
              {!plain && !noLabel && !inline && (inline || outline || plain) && (
                <label
                  data-testid={buildAutomationChildTestId(fieldTestId, "label")}
                  onClick={() => (!onFocus ? selectRef.current.focus() : null)}
                  style={
                    isDisabled
                      ? { backgroundColor: "#F6F6F6" }
                      : {}
                  }
                  className={
                    value || onFocus
                      ? "selectfield__label px-2 selectfield__notempty_input"
                      : "selectfield__label px-2"
                  }
                >
                  {t(label)}
                  {required && <font color="red">*</font>}
                </label>
              )}
            </div>
            {RightComponent && !inline && (
              <div
                id="x6"
                data-testid={buildAutomationChildTestId(fieldTestId, "right")}
                onClick={(e) =>
                  onClickRight ? onClickRight(e) : null
                }
                style={{ position: "relative", height: inline ? "19px" : "28px" }}
                className={
                  onClickRight
                    ? "component_pointer Selectfield__rightComponent"
                    : "selectfield__rightComponent"
                }
              >
                < RightComponent />
              </div>
            )}
          </div>
       
        </div>
        {desc ? <em className="inputfield__desc d-block" data-testid={buildAutomationChildTestId(fieldTestId, "description")}>
          {desc ? t(desc) : ""}
        </em> : null}

        {!suppressPreviousValue && (
          <PreviousValue
            show={prevValue?.has}
            testId={buildAutomationChildTestId(fieldTestId, "previous")}
            value={
              valueToLabelMap[prevValue?.value] ??
              `${prevValue?.value ? `"${prevValue?.value}"` : ""}`
            }
          />
        )}

        {error && showErr && (
          <em className="selectfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{t(error)}</em>
        )}
        {flexible && (
          <button data-testid={buildAutomationChildTestId(fieldTestId, "add")} onClick={() => setShowAddComponent(true)}>Add Entity</button>
        )}
      </div>
      {showAddComponent && flexible && AddEntityComponent && (
        <section
          data-testid={buildAutomationChildTestId(fieldTestId, "add-entity")}
          style={{
            height: "100vh",
            width: "100vw",
            backgroundColor: "white",
            position: "fixed",
            top: "0",
            left: "0",
            zIndex: "1500",
          }}
        >
          < AddEntityComponent setShowAddComponent={setShowAddComponent} />
        </section>
      )}
    </React.Fragment>
  );
};
const stylesForMain = {
  control: (base) => ({
    ...base,
    border: 0,
    boxShadow: "none",
    height: 24,
    maxHeight: 24,
    minHeight: 24,
    backgroundColor: "transparent !important"
  }),
  singleValue: (base) => ({ ...base, backgroundColor: "transparent !important" }),
  indicatorsContainer: (base) => ({ ...base, height: 22 }),
  valueContainer: (base) => ({ ...base, padding: 0, paddingLeft: "calc(1rem - 3px)", alignItems: "start", backgroundColor: "transparent !important" }),
  placeholder: (base) => ({ ...base, paddingLeft: 0, backgroundColor: "transparent !important" }),
  menu: (provided, state) => ({
    ...provided,
    width: "100%",
    color: state.selectProps.menuColor,
  }),
};

const stylesForInputGroup = {
  control: (base) => ({
    ...base,
    border: 0,
    boxShadow: "none",
    height: 24,
    maxHeight: 24,
    minHeight: 24,
    backgroundColor: "transparent !important",
  }),
  singleValue: (base) => ({ ...base, backgroundColor: "transparent !important", }),
  indicatorsContainer: (base) => ({ ...base, height: 22, backgroundColor: "transparent !important", }),
  valueContainer: (base) => ({ ...base, padding: 0, paddingLeft: "0px", 
    backgroundColor: "transparent !important"  ,
   alignItems : "start"}),
  placeholder: (base) => ({ ...base, paddingLeft: 0, backgroundColor: "transparent !important", }),
  menu: (provided, state) => ({
    ...provided,
    width: "130%",
    minWidth: 'max-content',
    color: state.selectProps.menuColor,
  }),
};

const stylesForInline = {
  control: (base) => ({
    ...base,
    border: "none",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    boxShadow: "none",
    minHeight: "18px",
    height: "18px",
    // width:'100%'
  }),
  container: (base) => ({
    ...base,
    border: "none",
  }),
  placeholder: (base) => ({ ...base, fontSize: 12 }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: "18px",
    minWidth: "40px",
    padding: "0 6px",
  }),
  menu: (provided, state) => ({
    ...provided,
    zIndex: 999
  }),

  input: (provided, state) => ({
    ...provided,
    margin: "0px",
    // height: "10px",
    padding: 0,
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    height: "18px",
  }),
};


const stylesForInputGroupChild = {
  control: (base) => ({
    ...base,
    border: 0,
    boxShadow: "none",
    height: 27,
    maxHeight: 27,
    minHeight: 27,
    fontSize
  }),
  groupHeading: (base) => ({
    ...base,
    color: '#808080',
    backgroundColor: "#EBEBEB",
    padding: "10px",
    margin: "0px",
    fontSize
  }),
  singleValue: (base) => ({ ...base, fontSize }),
  indicatorsContainer: (base) => ({ ...base, height: 22, paddingTop: 10, }),
  valueContainer: (base) => ({
    ...base,
    padding: 6, paddingLeft: "calc(1rem - 3px)", fontSize,
    backgroundColor: "transparent !important",
    alignItems: "start"
  }),
  placeholder: (base) => ({ ...base, paddingLeft: 0 }),
  menu: (provided, state) => ({
    ...provided,
    width: "100%",
    color: state.selectProps.menuColor,
  }),
};
